import { useState } from 'react';
import { cn } from '@/lib/utils';

interface AvatarProps {
  src?: string | null;
  firstName?: string;
  lastName?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const sizeClasses = {
  xs: 'h-6 w-6 text-[10px]',
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
  xl: 'h-32 w-32 text-3xl',
};

/**
 * Get initials from first and last name
 */
function getInitials(firstName?: string, lastName?: string): string {
  const first = firstName?.trim()?.[0] || '';
  const last = lastName?.trim()?.[0] || '';
  return (first + last).toUpperCase() || '?';
}

/**
 * Avatar component with image support and initials fallback
 * Uses brand gradient background for initials
 */
export function Avatar({ src, firstName, lastName, size = 'md', className }: AvatarProps) {
  const [imageError, setImageError] = useState(false);
  const showImage = src && !imageError;

  return (
    <div
      className={cn(
        'relative rounded-full overflow-hidden flex items-center justify-center',
        'bg-gradient-to-r from-[#17DDAB] to-[#009FE4]',
        sizeClasses[size],
        className
      )}
    >
      {showImage ? (
        <img
          src={src}
          alt={`${firstName || ''} ${lastName || ''}`.trim() || 'Avatar'}
          className="h-full w-full object-cover"
          onError={() => setImageError(true)}
        />
      ) : (
        <span className="font-medium text-white select-none">
          {getInitials(firstName, lastName)}
        </span>
      )}
    </div>
  );
}

export default Avatar;
